import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabase';

export interface UserEmail {
  id: string;
  email: string;
  email_normalized: string;
  is_verified: boolean;
  is_primary: boolean;
  is_active: boolean;
  label?: string;
  work_experience_id?: string;
  last_used_at?: string;
  created_at: string;
  bounce_type?: string;
  bounced_at?: string;
}

export function useUserEmails() {
  const queryClient = useQueryClient();
  
  // Get all emails for current user
  const { data: emails = [], isLoading, error } = useQuery({
    queryKey: ['userEmails'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_emails')
        .select('*')
        .order('is_primary', { ascending: false })
        .order('created_at');
      
      if (error) throw error;
      return data as UserEmail[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Add new email
  const addEmail = useMutation({
    mutationFn: async (email: string) => {
      // Call Edge Function to handle addition (includes duplicate check)
      const { data, error } = await supabase.functions.invoke('add-user-email', {
        body: { email }
      });
      
      if (error) {
        // Parse the error to check if it's a limit error
        const errorMessage = error.message || '';
        if (errorMessage.includes('Maximum of 5 email addresses')) {
          throw new Error('You have reached the maximum of 5 email addresses per account.');
        }
        if (errorMessage.includes('Too many verification attempts')) {
          throw new Error('Too many verification attempts for this email. Please try again tomorrow.');
        }
        throw error;
      }
      
      // Always show same message to prevent enumeration
      return { 
        message: 'If this email belongs to you, a verification email has been sent.' 
      };
    },
    onSuccess: (data) => {
      // Refetch emails to show the new pending email
      queryClient.invalidateQueries({ queryKey: ['userEmails'] });
    }
  });

  // Remove email
  const removeEmail = useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from('user_emails')
        .delete()
        .eq('id', emailId)
        .eq('is_primary', false); // Can't delete primary
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userEmails'] });
    }
  });

  // Update email settings (label, work experience)
  const updateEmailSettings = useMutation({
    mutationFn: async ({ 
      emailId, 
      label, 
      work_experience_id 
    }: { 
      emailId: string; 
      label?: string; 
      work_experience_id?: string | null;
    }) => {
      const { error } = await supabase
        .from('user_emails')
        .update({
          label,
          work_experience_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', emailId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userEmails'] });
    }
  });

  // Resend verification email
  const resendVerification = useMutation({
    mutationFn: async (emailId: string) => {
      // Find the email
      const email = emails.find(e => e.id === emailId);
      if (!email) throw new Error('Email not found');
      
      // Call add-user-email again - it will handle sending a new verification
      const { data, error } = await supabase.functions.invoke('add-user-email', {
        body: { email: email.email }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Success is handled by the component
    }
  });

  return {
    emails,
    isLoading,
    error,
    addEmail: addEmail.mutateAsync,
    removeEmail: removeEmail.mutateAsync,
    updateEmail: updateEmailSettings.mutateAsync,
    resendVerification: resendVerification.mutateAsync
  };
}